import type {
  Map as MapLibreMap,
  FillLayerSpecification,
  LineLayerSpecification,
  CircleLayerSpecification,
} from "maplibre-gl";
import type { ArcgisLayerConfig } from "@/types/layers";

interface ArcGISSimpleMarkerSymbol {
  type: "esriSMS";
  style: string;
  color: number[];
  size: number;
  outline?: {
    color: number[];
    width: number;
  };
}

interface ArcGISPictureMarkerSymbol {
  type: "esriPMS";
  url?: string;
  imageData?: string;
  contentType?: string;
  width: number;
  height: number;
  angle?: number;
  xoffset?: number;
  yoffset?: number;
}

interface ArcGISSimpleLineSymbol {
  type: "esriSLS";
  style: string;
  color: number[];
  width: number;
}

interface ArcGISSimpleFillSymbol {
  type: "esriSFS";
  style: string;
  color: number[];
  outline?: {
    type: string;
    style: string;
    color: number[];
    width: number;
  };
}

type ArcGISSymbol =
  | ArcGISSimpleMarkerSymbol
  | ArcGISPictureMarkerSymbol
  | ArcGISSimpleLineSymbol
  | ArcGISSimpleFillSymbol;

interface ArcGISDrawingInfo {
  renderer?: {
    type: string;
    symbol?: ArcGISSymbol;
    defaultSymbol?: ArcGISSymbol;
  };
}

interface ArcGISLayerInfo {
  id: number;
  name: string;
  type: string;
  geometryType: string;
  drawingInfo?: ArcGISDrawingInfo;
  minScale?: number;
  maxScale?: number;
}

interface ArcGISQueryResponse {
  geometryType?: string;
  error?: {
    code: number;
    message: string;
    details?: string[];
  };
}

interface LayerStyle {
  geometryType: "Point" | "LineString" | "Polygon";
  paint: {
    fill?: Record<string, unknown>;
    line?: Record<string, unknown>;
    circle?: Record<string, unknown>;
  };
  minZoom?: number;
  maxZoom?: number;
}

/**
 * Convert ArcGIS color array [r, g, b, a] to CSS rgba string
 */
function arcgisColorToRgba(color: number[]): string {
  if (!color || color.length < 3) {
    return "rgba(0, 0, 0, 1)";
  }
  const [r, g, b, a = 255] = color;
  return `rgba(${r}, ${g}, ${b}, ${a / 255})`;
}

/**
 * Convert ArcGIS scale to approximate MapLibre zoom level
 */
function scaleToZoom(scale: number): number {
  // Web Mercator scale formula: scale = 591657527.591555 / (2^zoom)
  // Solving for zoom: zoom = log2(591657527.591555 / scale)
  return Math.log2(591657527.591555 / scale);
}

/**
 * Fetch layer information from ArcGIS FeatureServer
 */
export async function fetchLayerInfo(
  url: string,
  layerNumber: number
): Promise<ArcGISLayerInfo | null> {
  try {
    // Check if URL already includes the layer number
    let layerUrl = url;
    if (!url.match(/\/\d+\/?$/)) {
      // URL doesn't end with a layer number, append it
      layerUrl = `${url}/${layerNumber}`;
    }

    const response = await fetch(`${layerUrl}?f=json`, {
      signal: AbortSignal.timeout(5000),
    });

    if (!response.ok) {
      console.warn(`Failed to fetch layer info for ${layerUrl}`);
      return null;
    }

    const data = await response.json();
    if (data?.error) {
      console.warn(
        `ArcGIS layer info error for ${layerUrl}: ${data.error.message} (code=${data.error.code})`
      );
      return null;
    }
    return data as ArcGISLayerInfo;
  } catch (error) {
    console.warn(`Error fetching layer info:`, error);
    return null;
  }
}

async function fetchLayerGeometryType(
  url: string,
  layerNumber: number,
  context: StyleContext
): Promise<string | null> {
  const { layerKey, layerNum } = context;
  const contextLabelParts = [
    layerKey ? `key=${layerKey}` : undefined,
    layerNum !== undefined ? `layer=${layerNum}` : undefined,
  ].filter(Boolean);
  const contextLabel = contextLabelParts.length
    ? ` (${contextLabelParts.join(", ")})`
    : "";

  try {
    // Check if URL already includes the layer number
    let baseUrl = url;
    if (!url.match(/\/\d+\/?$/)) {
      // URL doesn't end with a layer number, append it
      baseUrl = `${url}/${layerNumber}`;
    }

    const queryUrl = `${baseUrl}/query?f=json&where=1=1&returnGeometry=false&outFields=OBJECTID&resultRecordCount=1`;
    const response = await fetch(queryUrl, {
      signal: AbortSignal.timeout(5000),
    });

    if (!response.ok) {
      console.warn(
        `[ArcGIS Style] Failed to fetch geometry type${contextLabel}: HTTP ${response.status}`
      );
      return null;
    }

    const data = (await response.json()) as ArcGISQueryResponse;
    if (data?.error) {
      console.warn(
        `[ArcGIS Style] Geometry query error${contextLabel}: ${data.error.message} (code=${data.error.code})`
      );
      return null;
    }

    if (typeof data.geometryType === "string" && data.geometryType.length > 0) {
      return data.geometryType;
    }

    console.debug(
      `[ArcGIS Style] Geometry query returned no geometryType${contextLabel}`
    );
    return null;
  } catch (error) {
    console.warn(
      `[ArcGIS Style] Failed to query geometry type${contextLabel}:`,
      error
    );
    return null;
  }
}

/**
 * Convert ArcGIS simple fill symbol to MapLibre fill paint
 */
function convertFillSymbol(
  symbol: ArcGISSimpleFillSymbol,
  opacity: number
): Record<string, unknown> {
  const fillColor = arcgisColorToRgba(symbol.color);
  const outlineColor = symbol.outline
    ? arcgisColorToRgba(symbol.outline.color)
    : "rgba(0, 0, 0, 1)";

  return {
    "fill-color": fillColor,
    "fill-opacity": opacity,
    "fill-outline-color": outlineColor,
  };
}

/**
 * Convert ArcGIS simple line symbol to MapLibre line paint
 */
function convertLineSymbol(
  symbol: ArcGISSimpleLineSymbol,
  opacity: number
): Record<string, unknown> {
  const lineColor = arcgisColorToRgba(symbol.color);

  return {
    "line-color": lineColor,
    "line-width": symbol.width ?? 2,
    "line-opacity": opacity,
  };
}

/**
 * Convert ArcGIS simple marker symbol to MapLibre circle paint
 */
function convertMarkerSymbol(
  symbol: ArcGISSimpleMarkerSymbol,
  opacity: number
): Record<string, unknown> {
  const circleColor = arcgisColorToRgba(symbol.color);
  const strokeColor = symbol.outline
    ? arcgisColorToRgba(symbol.outline.color)
    : "rgba(0, 0, 0, 1)";
  const strokeWidth = symbol.outline?.width ?? 1;

  return {
    "circle-radius": symbol.size ?? 6,
    "circle-color": circleColor,
    "circle-stroke-color": strokeColor,
    "circle-stroke-width": strokeWidth,
    "circle-opacity": opacity,
  };
}

/**
 * Convert ArcGIS picture marker symbol to MapLibre circle paint
 * (fallback representation since MapLibre doesn't natively support icon images from URL)
 */
function convertPictureMarkerSymbol(
  symbol: ArcGISPictureMarkerSymbol,
  opacity: number
): Record<string, unknown> {
  // Use a clean, professional blue style for picture markers
  // Size is based on the image dimensions (usually 15-20px for markers)
  const size = Math.min(Math.max(symbol.width, symbol.height) / 3, 6) || 5;

  return {
    "circle-radius": size,
    "circle-color": "rgba(59, 130, 246, 0.85)", // Clean blue (Tailwind blue-500)
    "circle-stroke-color": "rgba(255, 255, 255, 0.9)",
    "circle-stroke-width": 1.5,
    "circle-opacity": opacity,
  };
}

/**
 * Convert ArcGIS layer info to MapLibre layer styles
 */
type StyleContext = {
  layerKey?: string;
  layerNum?: number;
};

export function convertArcGISStyleToMapLibre(
  layerInfo: ArcGISLayerInfo,
  opacity: number = 1.0,
  context: StyleContext = {}
): LayerStyle | null {
  const { layerKey, layerNum } = context;
  const contextLabelParts = [
    layerKey ? `key=${layerKey}` : undefined,
    layerInfo.name ? `name=${layerInfo.name}` : undefined,
    layerNum !== undefined ? `layer=${layerNum}` : undefined,
  ].filter(Boolean);
  const contextLabel = contextLabelParts.length
    ? ` (${contextLabelParts.join(", ")})`
    : "";

  // Extract symbol from renderer (used both for styling and geometry inference)
  const renderer = layerInfo.drawingInfo?.renderer;
  const symbol = renderer?.symbol ?? renderer?.defaultSymbol;

  // Determine geometry type
  let geometryType: "Point" | "LineString" | "Polygon" | undefined;
  if (layerInfo.geometryType === "esriGeometryPoint") {
    geometryType = "Point";
  } else if (
    layerInfo.geometryType === "esriGeometryPolyline" ||
    layerInfo.geometryType === "esriGeometryLine"
  ) {
    geometryType = "LineString";
  } else if (layerInfo.geometryType === "esriGeometryPolygon") {
    geometryType = "Polygon";
  } else if (!layerInfo.geometryType && symbol?.type) {
    // Some services omit geometryType but still provide renderer symbols.
    const symbolType = symbol.type.toLowerCase();
    if (symbolType === "esrisfs") {
      geometryType = "Polygon";
    } else if (symbolType === "esrisls") {
      geometryType = "LineString";
    } else if (symbolType === "esrisms") {
      geometryType = "Point";
    }
  }

  if (!geometryType) {
    const layerType = layerInfo.type ?? "unknown";
    // Skip tables or non-spatial layers silently
    if (typeof layerType === "string" && layerType.includes("Table")) {
      console.debug(
        `[ArcGIS Style] Skipping non-spatial table layer${contextLabel}`
      );
      return null;
    }

    console.warn(
      `[ArcGIS Style] Unknown geometry type${contextLabel}: geometryType=${layerInfo.geometryType}, layerType=${layerType}`
    );
    return null;
  }

  if (!symbol) {
    console.warn(`No symbol found for layer${contextLabel}`);
    return null;
  }

  const paint: LayerStyle["paint"] = {};

  // Convert symbol based on type
  if (symbol.type === "esriSFS" && geometryType === "Polygon") {
    paint.fill = convertFillSymbol(symbol as ArcGISSimpleFillSymbol, opacity);
  } else if (symbol.type === "esriSLS" && geometryType === "LineString") {
    paint.line = convertLineSymbol(symbol as ArcGISSimpleLineSymbol, opacity);
  } else if (symbol.type === "esriSMS" && geometryType === "Point") {
    paint.circle = convertMarkerSymbol(
      symbol as ArcGISSimpleMarkerSymbol,
      opacity
    );
  } else if (symbol.type === "esriPMS" && geometryType === "Point") {
    // Picture Marker Symbol - convert to simple circle
    paint.circle = convertPictureMarkerSymbol(
      symbol as ArcGISPictureMarkerSymbol,
      opacity
    );
  } else {
    console.warn(`[ArcGIS Style] Symbol type mismatch${contextLabel}:`, {
      symbolType: symbol.type,
      geometryType,
    });
  }

  // Convert scale-based visibility to zoom levels
  const result: LayerStyle = {
    geometryType,
    paint,
  };

  if (layerInfo.minScale) {
    result.maxZoom = scaleToZoom(layerInfo.minScale);
  }

  if (layerInfo.maxScale) {
    result.minZoom = scaleToZoom(layerInfo.maxScale);
  }

  return result;
}

/**
 * Fetch and apply ArcGIS styles for all layers in a FeatureServer
 */
export async function fetchAndApplyArcGISStyles(
  config: ArcgisLayerConfig,
  sourceId: string,
  map: MapLibreMap
): Promise<{
  geometryType: "Point" | "LineString" | "Polygon" | "Mixed";
  layerIds: string[];
}> {
  const layerNumbers =
    config.layer_numbers && config.layer_numbers.length > 0
      ? config.layer_numbers
      : [0];

  const layerStyles: LayerStyle[] = [];
  const layerIds: string[] = [];

  // Fetch styles for all layers
  for (const layerNum of layerNumbers) {
    const layerInfo = await fetchLayerInfo(config.url, layerNum);

    if (layerInfo) {
      let resolvedLayerInfo = layerInfo;
      if (!layerInfo.geometryType) {
        const inferredGeometry = await fetchLayerGeometryType(
          config.url,
          layerNum,
          {
            layerKey: config.key,
            layerNum,
          }
        );
        if (inferredGeometry) {
          resolvedLayerInfo = {
            ...layerInfo,
            geometryType: inferredGeometry,
          };
        }
      }
      const style = convertArcGISStyleToMapLibre(
        resolvedLayerInfo,
        config.opacity ?? 1.0,
        {
          layerKey: config.key,
          layerNum,
        }
      );
      if (style) {
        layerStyles.push(style);
      }
    }
  }

  // If no styles were fetched, use defaults
  if (layerStyles.length === 0) {
    return createDefaultLayers(config, sourceId, map);
  }

  // Determine overall geometry type
  const geometryTypes = new Set(layerStyles.map((s) => s.geometryType));
  const geometryType: "Point" | "LineString" | "Polygon" | "Mixed" =
    geometryTypes.size === 1 ? Array.from(geometryTypes)[0] : "Mixed";

  // Create layers based on fetched styles
  const hasPolygon = layerStyles.some((s) => s.geometryType === "Polygon");
  const hasLine = layerStyles.some((s) => s.geometryType === "LineString");
  const hasPoint = layerStyles.some((s) => s.geometryType === "Point");

  if (hasPolygon) {
    const polygonStyle = layerStyles.find((s) => s.geometryType === "Polygon");
    if (polygonStyle?.paint.fill) {
      const fillLayerId = `arcgis-${config.key}-fill`;
      if (!map.getLayer(fillLayerId)) {
        const layerSpec: FillLayerSpecification = {
          id: fillLayerId,
          type: "fill",
          source: sourceId,
          filter: [
            "match",
            ["geometry-type"],
            ["Polygon", "MultiPolygon"],
            true,
            false,
          ],
          paint: polygonStyle.paint.fill as FillLayerSpecification["paint"],
        };

        if (typeof polygonStyle.minZoom === "number") {
          layerSpec.minzoom = polygonStyle.minZoom;
        }
        if (typeof polygonStyle.maxZoom === "number") {
          layerSpec.maxzoom = polygonStyle.maxZoom;
        }

        map.addLayer(layerSpec);
        layerIds.push(fillLayerId);

        map.on("mouseenter", fillLayerId, () => {
          map.getCanvas().style.cursor = "pointer";
        });
        map.on("mouseleave", fillLayerId, () => {
          map.getCanvas().style.cursor = "";
        });
      }
    }
  }

  if (hasLine) {
    const lineStyle = layerStyles.find((s) => s.geometryType === "LineString");
    if (lineStyle?.paint.line) {
      const lineLayerId = `arcgis-${config.key}-line`;
      if (!map.getLayer(lineLayerId)) {
        const layerSpec: LineLayerSpecification = {
          id: lineLayerId,
          type: "line",
          source: sourceId,
          filter: [
            "match",
            ["geometry-type"],
            ["LineString", "MultiLineString"],
            true,
            false,
          ],
          paint: lineStyle.paint.line as LineLayerSpecification["paint"],
        };

        if (typeof lineStyle.minZoom === "number") {
          layerSpec.minzoom = lineStyle.minZoom;
        }
        if (typeof lineStyle.maxZoom === "number") {
          layerSpec.maxzoom = lineStyle.maxZoom;
        }

        map.addLayer(layerSpec);
        layerIds.push(lineLayerId);

        map.on("mouseenter", lineLayerId, () => {
          map.getCanvas().style.cursor = "pointer";
        });
        map.on("mouseleave", lineLayerId, () => {
          map.getCanvas().style.cursor = "";
        });
      }
    }
  }

  if (hasPoint) {
    const pointStyle = layerStyles.find((s) => s.geometryType === "Point");
    if (pointStyle?.paint.circle) {
      const pointLayerId = `arcgis-${config.key}-point`;

      if (!map.getLayer(pointLayerId)) {
        const layerSpec: CircleLayerSpecification = {
          id: pointLayerId,
          type: "circle",
          source: sourceId,
          filter: [
            "match",
            ["geometry-type"],
            ["Point", "MultiPoint"],
            true,
            false,
          ],
          paint: pointStyle.paint.circle as CircleLayerSpecification["paint"],
        };

        if (typeof pointStyle.minZoom === "number") {
          layerSpec.minzoom = pointStyle.minZoom;
        }
        if (typeof pointStyle.maxZoom === "number") {
          layerSpec.maxzoom = pointStyle.maxZoom;
        }

        map.addLayer(layerSpec);
        layerIds.push(pointLayerId);

        map.on("mouseenter", pointLayerId, () => {
          map.getCanvas().style.cursor = "pointer";
        });
        map.on("mouseleave", pointLayerId, () => {
          map.getCanvas().style.cursor = "";
        });
      }
    }
  }

  return { geometryType, layerIds };
}

/**
 * Create default layers when no styles are available
 */
function createDefaultLayers(
  config: ArcgisLayerConfig,
  sourceId: string,
  map: MapLibreMap
): {
  geometryType: "Mixed";
  layerIds: string[];
} {
  const layerIds: string[] = [];

  const fillLayerId = `arcgis-${config.key}-fill`;
  const lineLayerId = `arcgis-${config.key}-line`;
  const pointLayerId = `arcgis-${config.key}-point`;

  // Add polygon layer
  if (!map.getLayer(fillLayerId)) {
    map.addLayer({
      id: fillLayerId,
      type: "fill",
      source: sourceId,
      filter: [
        "match",
        ["geometry-type"],
        ["Polygon", "MultiPolygon"],
        true,
        false,
      ],
      paint: {
        "fill-color": "#3B82F6",
        "fill-opacity": config.opacity ?? 0.4,
        "fill-outline-color": "#1E40AF",
      },
    });
    layerIds.push(fillLayerId);

    map.on("mouseenter", fillLayerId, () => {
      map.getCanvas().style.cursor = "pointer";
    });
    map.on("mouseleave", fillLayerId, () => {
      map.getCanvas().style.cursor = "";
    });
  }

  // Add line layer
  if (!map.getLayer(lineLayerId)) {
    map.addLayer({
      id: lineLayerId,
      type: "line",
      source: sourceId,
      filter: [
        "match",
        ["geometry-type"],
        ["LineString", "MultiLineString"],
        true,
        false,
      ],
      paint: {
        "line-color": "#3B82F6",
        "line-width": 2,
        "line-opacity": config.opacity ?? 0.8,
      },
    });
    layerIds.push(lineLayerId);

    map.on("mouseenter", lineLayerId, () => {
      map.getCanvas().style.cursor = "pointer";
    });
    map.on("mouseleave", lineLayerId, () => {
      map.getCanvas().style.cursor = "";
    });
  }

  // Add point layer
  if (!map.getLayer(pointLayerId)) {
    map.addLayer({
      id: pointLayerId,
      type: "circle",
      source: sourceId,
      filter: [
        "match",
        ["geometry-type"],
        ["Point", "MultiPoint"],
        true,
        false,
      ],
      paint: {
        "circle-radius": 6,
        "circle-color": "#3B82F6",
        "circle-stroke-color": "#1E40AF",
        "circle-stroke-width": 2,
        "circle-opacity": config.opacity ?? 0.8,
      },
    });
    layerIds.push(pointLayerId);

    map.on("mouseenter", pointLayerId, () => {
      map.getCanvas().style.cursor = "pointer";
    });
    map.on("mouseleave", pointLayerId, () => {
      map.getCanvas().style.cursor = "";
    });
  }

  return { geometryType: "Mixed", layerIds };
}
